import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  LogIn,
  Shield,
  Activity,
  Gauge,
  KeyRound,
  Sparkles,
  Brain,
  FlaskConical,
} from 'lucide-react'
import RyedoreLogo from '@/components/common/RyedoreLogo'

// v49: build-time spoke flag — login page is pre-auth so we can't use the
// useDeploymentMode hook (which needs the API).  VITE_BUILD_MODE is set by
// the npm script (`build:spoke`) so it's reliable across deployments.
const IS_SPOKE_BUILD = import.meta.env.VITE_BUILD_MODE === 'spoke'

/**
 * Login — production-grade sign-in screen.
 *
 * Layout: a two-column shell on ≥md viewports — the right column hosts the
 * brand panel (full Ryedore wordmark + product positioning + capability
 * highlights), the left column hosts the credentials form. Below md it
 * collapses to a single column with the wordmark above the form.
 *
 * Polish (Apr 2026):
 *   - Wordmark variant of <RyedoreLogo /> for stronger brand presence
 *   - Capability strip (Brain · Playground · Arc) replaces the generic
 *     "Real-time Monitoring / Predictive Analytics / Enterprise Security"
 *     trio so first-time users immediately see what makes Ryedore distinct
 *   - Trust footer surfaces version + deployment mode for support handoff
 */
export default function Login() {
  const { t } = useTranslation('common')
  const { login } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [mfaToken, setMfaToken] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [mfaRequired, setMfaRequired] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const result = await login(username, password, mfaToken || undefined)

      if (result.success) {
        // Login successful - redirect handled by AuthContext
      } else if (result.mfaRequired) {
        setMfaRequired(true)
        setError('Please enter your 6-digit authentication code')
      } else {
        setError(result.error || 'Invalid username or password')
      }
    } catch (err: any) {
      if (err?.response?.status === 403 && err?.response?.data?.detail?.includes('MFA')) {
        setMfaRequired(true)
        setError('Please enter your 6-digit authentication code')
      } else {
        setError(err?.response?.data?.detail || 'An error occurred. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToLogin = () => {
    setMfaRequired(false)
    setMfaToken('')
    setError('')
  }

  // Read deployment mode from a Vite-injected env var so the footer
  // honestly says "Spoke" or "Hub" without a roundtrip to /api/v2/health.
  const deploymentMode =
    (import.meta as ImportMeta & { env: Record<string, string> }).env
      .VITE_DEPLOYMENT_MODE || 'spoke'
  const versionTag =
    (import.meta as ImportMeta & { env: Record<string, string> }).env
      .VITE_APP_VERSION || 'v2.0.3'

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col md:flex-row">
      {/* ─── Brand panel (right side on md+, top on mobile) ─────────── */}
      <div
        className="relative md:w-1/2 lg:w-[55%] flex flex-col justify-center px-8 py-10 md:px-16 lg:px-24 overflow-hidden"
        aria-hidden="false"
      >
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.6\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            }}
          />
        </div>
        {/* Decorative gradient blobs */}
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-xl">
          <RyedoreLogo variant="wordmark" size={64} className="mb-6" />
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-white mb-3">
            Industrial intelligence that learns from your assets.
          </h1>
          <p className="text-base text-slate-400 leading-relaxed mb-8">
            Physics-grounded predictions, generative design, and an autonomous
            agent — running locally on your data, with every decision
            explainable and auditable.
          </p>

          {/* v49: capability chips — IP-masked on Spoke (no internal product
              names, no specific component counts).  Hub keeps the rich
              internal labelling. "Oracle" replaced with "Arc" everywhere
              (Oracle was the legacy name for the agentic console). */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
            <CapabilityChip
              icon={<Brain className="w-5 h-5 text-blue-400" />}
              title={IS_SPOKE_BUILD ? 'AI Intelligence' : 'Brain Intelligence'}
              detail={IS_SPOKE_BUILD ? 'Continuous learning, fully audited' : '32 components, full audit'}
            />
            <CapabilityChip
              icon={<FlaskConical className="w-5 h-5 text-emerald-400" />}
              title={IS_SPOKE_BUILD ? 'Investigation Workspace' : 'Investigation Playground'}
              detail="Ryekronix · Twin · Hypothesis"
            />
            <CapabilityChip
              icon={<Sparkles className="w-5 h-5 text-violet-400" />}
              title={t('title_arc_agent')}
              detail="Autonomous goal execution"
            />
          </div>

          {/* v49: trust signals — industry-grade compliance / deployment
              posture, helps senior buyers feel safe at sign-in. */}
          <div className="hidden md:flex items-center gap-4 mb-6 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Local deployment
            </span>
            <span className="text-slate-700">·</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-400" />
              SOC&nbsp;2 / ISO&nbsp;27001 ready
            </span>
            <span className="text-slate-700">·</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-violet-400" />
              Decision-grade audit
            </span>
          </div>

          <div className="hidden md:grid grid-cols-3 gap-6 max-w-lg pt-6 border-t border-slate-800">
            <FeatureCallout
              icon={<Gauge className="w-4 h-4 text-blue-400" />}
              label={t('label_real_time_monitoring')}
            />
            <FeatureCallout
              icon={<Activity className="w-4 h-4 text-emerald-400" />}
              label={t('label_predictive_analytics')}
            />
            <FeatureCallout
              icon={<Shield className="w-4 h-4 text-violet-400" />}
              label={t('label_enterprise_security')}
            />
          </div>
        </div>
      </div>

      {/* ─── Auth panel (left on md+, bottom on mobile) ─────────────── */}
      <div className="md:w-1/2 lg:w-[45%] flex items-center justify-center px-6 py-10 bg-slate-900/50 border-t md:border-t-0 md:border-l border-slate-800/80">
        <div className="w-full max-w-md">
          <div className="bg-slate-800/60 backdrop-blur-xl border border-slate-700/60 rounded-2xl shadow-2xl shadow-black/40 p-8">
            {/* Mobile-only mark — wordmark already lives in the brand panel */}
            <div className="text-center mb-6 md:hidden">
              <RyedoreLogo variant="mark" size={56} className="mx-auto" />
            </div>

            <h2 className="text-xl font-semibold text-white mb-1">
              {mfaRequired ? 'Two-factor authentication' : 'Sign in'}
            </h2>
            <p className="text-sm text-slate-400 mb-6">
              {mfaRequired
                ? 'Enter the 6-digit code from your authenticator app.'
                : 'Welcome back. Sign in to continue to your workspace.'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!mfaRequired ? (
                <>
                  <div>
                    <label
                      htmlFor="username"
                      className="block text-sm font-medium text-slate-300 mb-1.5"
                    >
                      Username
                    </label>
                    <input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder={t('placeholder_enter_your_username')}
                      autoComplete="username"
                      autoFocus
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-slate-300 mb-1.5"
                    >
                      Password
                    </label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('placeholder_enter_your_password')}
                      autoComplete="current-password"
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                      required
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-center mb-1">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-full">
                      <KeyRound className="w-6 h-6 text-blue-400" />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="mfaToken"
                      className="block text-sm font-medium text-slate-300 mb-1.5"
                    >
                      Authentication Code
                    </label>
                    <input
                      id="mfaToken"
                      type="text"
                      inputMode="numeric"
                      value={mfaToken}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                        setMfaToken(value)
                      }}
                      placeholder="000000"
                      maxLength={6}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white text-center text-2xl tracking-[0.4em] placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all font-mono"
                      required
                      autoFocus
                      autoComplete="one-time-code"
                    />
                    <p className="text-xs text-slate-400 mt-2 text-center">
                      Signed in as{' '}
                      <span className="text-white font-medium">{username}</span>
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="w-full text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    ← Back to sign-in
                  </button>
                </>
              )}

              {error && (
                <div
                  className={`flex items-start gap-2 ${
                    mfaRequired
                      ? 'text-blue-300 bg-blue-500/10 border-blue-500/30'
                      : 'text-red-300 bg-red-500/10 border-red-500/30'
                  } border rounded-lg px-4 py-3`}
                  role="alert"
                >
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || (mfaRequired && mfaToken.length !== 6)}
                className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-busy={isLoading}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    {mfaRequired ? (
                      <KeyRound className="w-4 h-4" />
                    ) : (
                      <LogIn className="w-4 h-4" />
                    )}
                    {mfaRequired ? 'Verify code' : 'Sign in'}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Trust footer — version + deployment mode aid support handoff. */}
          <div className="mt-6 flex items-center justify-center gap-3 text-xs text-slate-500">
            <span>{t('ryedore_industrial_intelligence')}</span>
            <span aria-hidden>·</span>
            <span className="font-mono">{versionTag}</span>
            <span aria-hidden>·</span>
            <span className="uppercase tracking-wider">{deploymentMode}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * CapabilityChip — small product-positioning tile in the brand panel.
 * Three of these answer "what's distinctive about Ryedore?" at a glance.
 */
function CapabilityChip({
  icon,
  title,
  detail,
}: {
  icon: React.ReactNode
  title: string
  detail: string
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2.5">
      <div className="flex-shrink-0 mt-0.5">{icon}</div>
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-100 leading-tight">
          {title}
        </div>
        <div className="text-xs text-slate-400 truncate">{detail}</div>
      </div>
    </div>
  )
}

/** FeatureCallout — secondary capability label below the chip strip. */
function FeatureCallout({
  icon,
  label,
}: {
  icon: React.ReactNode
  label: string
}) {
  return (
    <div className="flex items-center gap-2 text-xs text-slate-400">
      {icon}
      <span>{label}</span>
    </div>
  )
}
